import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import { Box, Card, TextField, Typography } from "@mui/material";
import { type FC, useEffect, useState } from "react";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { INK, NEUTRAL, SURFACE } from "../../../theme/tokens";
import { COLORS, ROLE_COLORS, type RoleRecommendation, SHIFTS_PER_NURSE_2WEEKS, SHIFTS_PER_WEEK, ShiftFilter } from "../types";
import { ShiftFilterChips } from "./ShiftFilterChips";

interface NewHireCardProps {
	recommendation: RoleRecommendation;
	onAccept: (role: string, count: number, filter: ShiftFilter) => void;
}

export const NewHireCard: FC<NewHireCardProps> = ({ recommendation, onAccept }) => {
	const [filter, setFilter] = useState<ShiftFilter>(ShiftFilter.All);
	const suggestedHireCount = recommendation.total > 0 ? Math.max(1, Math.ceil(recommendation.total / SHIFTS_PER_NURSE_2WEEKS)) : 1;
	const [hireCount, setHireCount] = useState<number>(suggestedHireCount);
	const [accepted, setAccepted] = useState(false);

	useEffect(() => {
		setHireCount(suggestedHireCount);
	}, [suggestedHireCount]);

	const color = ROLE_COLORS[recommendation.role] ?? COLORS.primary;

	const handleAccept = () => {
		setAccepted(true);
		onAccept(recommendation.role, hireCount, filter);
	};

	return (
		<Card
			sx={{
				p: 2,
				border: accepted ? `1px solid ${color}60` : `1px solid ${NEUTRAL[200]}`,
				bgcolor: accepted ? `${color}08` : SURFACE,
				boxShadow: "none",
				borderRadius: 1,
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<Box
						sx={{
							px: 1.5,
							py: 0.3,
							borderRadius: "6px",
							bgcolor: `${color}18`,
							border: `1px solid ${color}40`,
						}}
					>
						<Typography sx={{ fontSize: "13px", fontWeight: 600, color }}>{recommendation.role}</Typography>
					</Box>
					<Box
						sx={{
							display: "flex",
							gap: 1,
						}}
					>
						<Typography sx={{ fontSize: "16px", fontWeight: 700, color: INK.PRIMARY }}>+{recommendation.day} day</Typography>
						<Typography sx={{ fontSize: "16px", fontWeight: 700, color: INK.PRIMARY }}>+{recommendation.night} night</Typography>
					</Box>
				</Box>
				{accepted ? (
					<CheckCircleIcon sx={{ color, fontSize: 22 }} />
				) : (
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color }}>
						<GroupOutlinedIcon sx={{ fontSize: 18 }} />
						<Typography sx={{ fontSize: "14px", fontWeight: 600 }}>{hireCount} total</Typography>
					</Box>
				)}
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: COLORS.textSecondary, mb: 1.5 }}>
				<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />
				<Typography sx={{ fontSize: "13px" }}>Each working {SHIFTS_PER_WEEK} shifts per week to fill coverage gaps</Typography>
			</Box>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
				<TextField
					type="number"
					size="small"
					label="Hire count"
					value={hireCount}
					onChange={(e) => setHireCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
					sx={{
						width: 100,
						"& .MuiInputBase-input": { py: 0.8, fontSize: "14px" },
						"& .MuiInputLabel-root": { fontSize: "13px" },
					}}
					slotProps={{
						htmlInput: { min: 1 },
					}}
				/>

				<ShiftFilterChips value={filter} onChange={setFilter} color={color} />

				<SystemButton
					size="small"
					buttonVariant="primary"
					disabled={accepted}
					onClick={handleAccept}
					sx={{
						ml: "auto",
						bgcolor: color,
						"&:hover": { bgcolor: color, opacity: 0.9 },
					}}
				>
					{accepted ? "Applied" : "Hire & Simulate"}
				</SystemButton>
			</Box>
		</Card>
	);
};
